/**
 * Window-expiry ring allocator.
 *
 * Spawn slots are handed out from free runs (never-used + expired windows).
 * Adjacent free runs coalesce. A window whose expiry has passed is
 * reclaimable regardless of where it sits in the ring — no GPU readback,
 * no "rewind only when live === 0". Unexpired windows are never overwritten.
 * When every window is still live, the request is scaled down to remaining
 * (possibly 0).
 */

export interface SpawnWindow {
  start: number;
  count: number;
  expiresAt: number;
}

export interface FreeRun {
  start: number;
  count: number;
}

export interface WindowRingAllocator {
  readonly capacity: number;
  readonly stepBudget: number;
  reset(): void;
  beginStep(): void;
  spawnedThisStep(): number;
  unexpiredCount(now: number): number;
  remaining(now: number): number;
  pressure(now: number): number;
  allocate(requested: number, now: number, expiresAt: number): number[];
  allocateWithLives(lives: readonly number[], now: number): number[];
  windows(): readonly SpawnWindow[];
}

function reclaimWindows(windows: SpawnWindow[], now: number): SpawnWindow[] {
  let kept = 0;
  for (let i = 0; i < windows.length; i += 1) {
    if (windows[i].expiresAt > now) {
      windows[kept] = windows[i];
      kept += 1;
    }
  }
  windows.length = kept;
  return windows;
}

/** Free runs in [0, capacity), windows must already be reclaimed + start-sorted. */
export function freeRunsFromWindows(windows: readonly SpawnWindow[], capacity: number): FreeRun[] {
  const runs: FreeRun[] = [];
  let cursor = 0;
  for (let i = 0; i < windows.length; i += 1) {
    const w = windows[i];
    if (w.start > cursor) runs.push({ start: cursor, count: w.start - cursor });
    cursor = Math.max(cursor, w.start + w.count);
  }
  if (cursor < capacity) runs.push({ start: cursor, count: capacity - cursor });
  return runs;
}

export function slotRuns(
  slots: readonly number[],
): Array<{ start: number; offset: number; count: number }> {
  if (slots.length === 0) return [];
  const runs: Array<{ start: number; offset: number; count: number }> = [];
  let offset = 0;
  let start = slots[0];
  let count = 1;
  for (let i = 1; i < slots.length; i += 1) {
    if (slots[i] === slots[i - 1] + 1) {
      count += 1;
      continue;
    }
    runs.push({ start, offset, count });
    offset = i;
    start = slots[i];
    count = 1;
  }
  runs.push({ start, offset, count });
  return runs;
}

function takeFromRuns(runs: readonly FreeRun[], n: number): number[] {
  const slots = new Array<number>(n);
  let filled = 0;
  for (let r = 0; r < runs.length && filled < n; r += 1) {
    const run = runs[r];
    const take = Math.min(run.count, n - filled);
    for (let i = 0; i < take; i += 1) slots[filled + i] = run.start + i;
    filled += take;
  }
  return slots;
}

function insertWindows(windows: SpawnWindow[], added: readonly SpawnWindow[]): void {
  if (added.length === 0) return;
  for (let i = 0; i < added.length; i += 1) windows.push(added[i]);
  windows.sort((a, b) => a.start - b.start || a.expiresAt - b.expiresAt);
  let kept = 0;
  for (let i = 0; i < windows.length; i += 1) {
    const w = windows[i];
    if (kept === 0) {
      windows[kept] = w;
      kept = 1;
      continue;
    }
    const prev = windows[kept - 1];
    if (prev.start + prev.count === w.start && prev.expiresAt === w.expiresAt) {
      prev.count += w.count;
    } else {
      windows[kept] = w;
      kept += 1;
    }
  }
  windows.length = kept;
}

function recordSlotWindows(
  slots: readonly number[],
  expires: readonly number[],
): SpawnWindow[] {
  if (slots.length === 0) return [];
  const added: SpawnWindow[] = [];
  let start = slots[0];
  let count = 1;
  let exp = expires[0];
  for (let i = 1; i < slots.length; i += 1) {
    if (slots[i] === slots[i - 1] + 1 && expires[i] === exp) {
      count += 1;
      continue;
    }
    added.push({ start, count, expiresAt: exp });
    start = slots[i];
    count = 1;
    exp = expires[i];
  }
  added.push({ start, count, expiresAt: exp });
  return added;
}

export function createWindowRingAllocator(
  capacity: number,
  stepBudget = Number.POSITIVE_INFINITY,
): WindowRingAllocator {
  const cap = Math.max(0, capacity | 0);
  const budget = stepBudget > 0 ? stepBudget : 0;
  const windows: SpawnWindow[] = [];
  let spawned = 0;

  const prepare = (now: number): FreeRun[] => {
    reclaimWindows(windows, now);
    return freeRunsFromWindows(windows, cap);
  };

  const occupiedNow = (): number => {
    let n = 0;
    for (let i = 0; i < windows.length; i += 1) n += windows[i].count;
    return n;
  };

  const claim = (requested: number, now: number): number[] => {
    if (cap <= 0 || requested <= 0) return [];
    const room = Math.min(requested | 0, budget - spawned);
    if (room <= 0) return [];
    const runs = prepare(now);
    let free = 0;
    for (let i = 0; i < runs.length; i += 1) free += runs[i].count;
    const occupied = occupiedNow();
    const reserve = occupied > 0 && cap >= 256 ? Math.min(free, Math.max(1, cap >> 4)) : 0;
    const usable = free > reserve ? free - reserve : free;
    const n = Math.min(room, usable);
    if (n <= 0) return [];
    const slots = takeFromRuns(runs, n);
    spawned += slots.length;
    return slots;
  };

  return {
    capacity: cap,
    stepBudget: budget,
    reset() {
      windows.length = 0;
      spawned = 0;
    },
    beginStep() {
      spawned = 0;
    },
    spawnedThisStep() {
      return spawned;
    },
    unexpiredCount(now) {
      reclaimWindows(windows, now);
      let n = 0;
      for (let i = 0; i < windows.length; i += 1) n += windows[i].count;
      return n;
    },
    remaining(now) {
      return Math.max(0, cap - this.unexpiredCount(now));
    },
    pressure(now) {
      return cap > 0 ? Math.min(1, Math.max(0, this.unexpiredCount(now) / cap)) : 0;
    },
    allocate(requested, now, expiresAt) {
      if (!(expiresAt > now)) return [];
      const slots = claim(requested, now);
      if (slots.length === 0) return slots;
      const expires = new Array<number>(slots.length);
      for (let i = 0; i < slots.length; i += 1) expires[i] = expiresAt;
      insertWindows(windows, recordSlotWindows(slots, expires));
      return slots;
    },
    allocateWithLives(lives, now) {
      if (lives.length === 0) return [];
      let want = 0;
      for (let i = 0; i < lives.length; i += 1) {
        if (now + lives[i] > now) want += 1;
      }
      const slots = claim(want, now);
      if (slots.length === 0) return slots;
      const expires = new Array<number>(slots.length);
      let src = 0;
      for (let i = 0; i < slots.length; i += 1) {
        while (src < lives.length && !(now + lives[src] > now)) src += 1;
        expires[i] = now + lives[src];
        src += 1;
      }
      insertWindows(windows, recordSlotWindows(slots, expires));
      return slots;
    },
    windows() {
      return windows;
    },
  };
}
